import { useQuery } from '@tanstack/react-query';
import { useState, useRef, useEffect } from 'react';
import { FlaskConical } from 'lucide-react';
import clsx from 'clsx';
import { serverInfoApi } from '../lib/api';

/**
 * Topbar badge that's only visible when the server reports
 * `devMode: true` (i.e. it was started with `ADH_DEV_MODE=1`). Clicking
 * the badge opens a popover listing the injected fake serials and the
 * operations that won't work against them — so users testing
 * claim/release/lease flows aren't surprised when scrcpy or `adb
 * install` errors out.
 *
 * The visibility check polls `/api/server-info` every 60s (same cadence
 * as `DeviceFilesDirSetting`); when the env flag flips off and the server
 * restarts, the badge silently disappears on the next poll.
 */
export function DevModeBadge() {
  const info = useQuery({
    queryKey: ['server-info', 'dev-mode'],
    queryFn: () => serverInfoApi.get(),
    staleTime: 60_000,
  });
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Click-outside-to-close. We listen on mousedown so a click on the
  // badge itself doesn't immediately close the popover we're trying
  // to open in the same gesture.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!info.data?.devMode) return null;
  const serials = info.data.fakeSerials ?? [];

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
          'border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-300',
          'hover:bg-amber-500/20 transition-colors'
        )}
        title="开发者模式已开启"
      >
        <FlaskConical className="h-3.5 w-3.5" />
        <span>开发者模式</span>
      </button>
      {open && (
        <div
          className={clsx(
            'absolute right-0 top-full z-50 mt-2 w-72 rounded-lg border border-line bg-bg-card p-3 shadow-lg',
            'text-xs text-ink'
          )}
        >
          <div className="mb-2 flex items-center gap-1.5 font-semibold text-amber-700 dark:text-amber-300">
            <FlaskConical className="h-3.5 w-3.5" />
            <span>开发者模式 · 假设备测试</span>
          </div>
          <p className="mb-2 text-ink-muted">
            服务端启用了 <code className="rounded bg-bg-soft px-1">ADH_DEV_MODE=1</code>，
            正在用假设备模拟共享池，便于双浏览器测 claim/release/lease。
          </p>
          {serials.length > 0 ? (
            <>
              <div className="mb-1 font-medium text-ink-muted">当前假设备</div>
              <ul className="mb-2 space-y-0.5">
                {serials.map((s) => (
                  <li key={s} className="font-mono">
                    {s}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mb-2 text-ink-muted">（未注入任何假设备）</p>
          )}
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] leading-relaxed text-ink-muted">
            <div className="mb-1 font-medium text-amber-700 dark:text-amber-300">
              已知限制
            </div>
            <ul className="list-disc space-y-0.5 pl-4">
              <li>镜像 / 安装 / logcat / shell 对这些设备会失败（预期内）</li>
              <li>仅可验证 registry / lease 状态变化</li>
              <li>关闭：去掉 <code className="rounded bg-bg-soft px-1">ADH_DEV_MODE</code> 后重启</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
